// src/features/settings/components/overenskomster-panel/OverenskomsterPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useOverenskomster } from '../../../tidsregistering/hooks/useOverenskomster';
import { useTidsregistreringOverenskomstPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import { useAuth } from '../../../../context/AuthContext';
import OverenskomstCreateForm from './OverenskomstCreateForm';
import OverenskomstEditDialog from './OverenskomstEditDialog';
import OverenskomsterGrid from './OverenskomsterGrid';

const EMPTY_FORM = {
  name: '',
  code: '',
  valid_from: '',
  valid_to: '',
  pension_rate_employer: 0,
  pension_rate_employee: 0,
  fritvalg_pct: 0,
  education_hours_per_year: 0,
  barsel_foedende_weeks: 0,
  barsel_ikke_foedende_weeks: 0,
  senior_years_before_pension: 0,
  senior_trigger_age: '',
  forskudttid_rate: 0,
  assumes_scheduled_break: false,
};

export default function OverenskomsterPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useTidsregistreringOverenskomstPermissions();

  const {
    overenskomster,
    loading,
    error,
    clearError,
    fetchForDepartment,
    createOverenskomst,
    updateOverenskomst,
    deleteOverenskomst,
    seedOverenskomster,
  } = useOverenskomster();

  const [addFormVisible, setAddFormVisible] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    fetchForDepartment();
  }, [fetchForDepartment]);

  const handleFormChange = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const handleCreate = async () => {
    const errors = {};
    if (!formValues.name.trim()) errors.name = t('settings:overenskomster.validation.name_required');
    if (!formValues.code.trim()) errors.code = t('settings:overenskomster.validation.code_required');
    if (!formValues.valid_from) errors.valid_from = t('settings:overenskomster.validation.valid_from_required');
    if (!formValues.valid_to) errors.valid_to = t('settings:overenskomster.validation.valid_to_required');
    if (formValues.valid_from && formValues.valid_to && formValues.valid_to <= formValues.valid_from) {
      errors.valid_to = t('settings:overenskomster.validation.valid_to_after_valid_from');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      ...formValues,
      senior_trigger_age: formValues.senior_trigger_age === '' ? null : Number(formValues.senior_trigger_age),
    };

    const result = await createOverenskomst(payload);
    if (result.success) {
      showSuccess(t('settings:overenskomster.success.created'));
      setFormValues(EMPTY_FORM);
      setAddFormVisible(false);
      fetchForDepartment();
    } else {
      showError(result.error);
    }
  };

  const handleEdit = useCallback((row) => {
    setEditRow(row);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditRow(null);
  }, []);

  const handleEditSubmit = useCallback(async (data) => {
    const result = await updateOverenskomst(editRow.overenskomst_id, data);
    if (result.success) {
      showSuccess(t('settings:overenskomster.success.updated'));
      setEditRow(null);
      fetchForDepartment();
    } else {
      showError(result.error);
    }
  }, [editRow, updateOverenskomst, showSuccess, showError, fetchForDepartment, t]);

  const handleSeed = useCallback(() => {
    showConfirmDialog({
      title: t('settings:overenskomster.confirm.seed_title'),
      message: t('settings:overenskomster.confirm.seed_message'),
      onConfirm: async () => {
        const result = await seedOverenskomster();
        if (result.success) {
          showSuccess(t('settings:overenskomster.success.seeded'));
          fetchForDepartment();
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, seedOverenskomster, fetchForDepartment, showSuccess, showError, t]);

  const handleCancelCreate = useCallback(() => {
    setFormValues(EMPTY_FORM);
    setFormErrors({});
    setAddFormVisible(false);
  }, []);

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('settings:overenskomster.confirm.delete_title'),
      message: t('settings:overenskomster.confirm.delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteOverenskomst(row.overenskomst_id);
        if (result.success) {
          showSuccess(t('settings:overenskomster.success.deleted'));
          fetchForDepartment();
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteOverenskomst, fetchForDepartment, showSuccess, showError, t]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('settings:overenskomster.description')}
        </Typography>

        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddFormVisible(true)}
                disabled={addFormVisible}
              >
                {t('settings:overenskomster.buttons.add')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleSeed}
                disabled={loading}
              >
                {t('settings:overenskomster.buttons.seed')}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchForDepartment()}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        <OverenskomstCreateForm
          visible={addFormVisible}
          values={formValues}
          errors={formErrors}
          loading={loading}
          onChange={handleFormChange}
          onSubmit={handleCreate}
          onCancel={handleCancelCreate}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <OverenskomsterGrid
          overenskomster={overenskomster}
          loading={loading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Paper>

      <OverenskomstEditDialog
        open={Boolean(editRow)}
        row={editRow}
        loading={loading}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
      />

      <ConfirmDialog />
    </Box>
  );
}
